import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";

import useData from "../hooks/useData";
import MUIIcon from "../components/ui/MUIIcon";

const PublicLinks = () => {
  const { executeAction } = useData();

  const normalizeLinkUrl = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^(https?:|mailto:|tel:)/i.test(raw)) return raw;
    return `https://${raw}`;
  };

  const [language, setLanguage] = useState("en");
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [passcode, setPasscode] = useState("");
  const [showPasscodeInput, setShowPasscodeInput] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    const loadLinks = async (nextPasscode = "") => {
      setLoading(true);
      setError("");
      setUnlockMessage("");

      try {
        const result = await executeAction({
          entityName: "PublicLink",
          action: "publicLinksList",
          data: {
            passcode: nextPasscode || undefined,
          },
        });

        if (result?.isSuccessful && Array.isArray(result.data)) {
          setLinks(result.data);
          if (result.message === "securedLinksUnlocked") {
            setUnlocked(true);
            setUnlockMessage(
              language === "fr"
                ? "Liens sécurisés débloqués"
                : "Secured links unlocked",
            );
          } else if (result.message === "invalidPasscode") {
            setUnlocked(false);
            setUnlockMessage(
              language === "fr" ? "Code d'accès invalide" : "Invalid passcode",
            );
          } else {
            setUnlocked(false);
          }
          return;
        }

        setLinks([]);
        setError(result?.message || "Unable to load links");
      } catch (err) {
        setLinks([]);
        setError(err.message || "Unable to load links");
      } finally {
        setLoading(false);
      }
    };

    loadLinks();
  }, []);

  const handleUnlock = async () => {
    await (async (input) => {
      setLoading(true);
      setError("");
      setUnlockMessage("");
      try {
        const result = await executeAction({
          entityName: "PublicLink",
          action: "publicLinksList",
          data: {
            passcode: input || undefined,
          },
        });

        if (result?.isSuccessful && Array.isArray(result.data)) {
          setLinks(result.data);
          if (result.message === "securedLinksUnlocked") {
            setUnlocked(true);
            setUnlockMessage(
              language === "fr"
                ? "Liens sécurisés débloqués"
                : "Secured links unlocked",
            );
          } else if (result.message === "invalidPasscode") {
            setUnlocked(false);
            setUnlockMessage(
              language === "fr" ? "Code d'accès invalide" : "Invalid passcode",
            );
          } else {
            setUnlocked(false);
          }
          return;
        }

        setLinks([]);
        setUnlocked(false);
        setError(result?.message || "Unable to load links");
      } catch (err) {
        setLinks([]);
        setUnlocked(false);
        setError(err.message || "Unable to load links");
      } finally {
        setLoading(false);
      }
    })(passcode);
  };

  const grouped = useMemo(() => {
    const map = new Map();

    for (const item of links) {
      const categoryEn = String(item?.category || "").trim() || "Other";
      const categoryFr = String(item?.category_fr || "").trim() || categoryEn;
      const categoryLabel = language === "fr" ? categoryFr : categoryEn;

      const nameEn = String(item?.name || "").trim() || item?.linkUrl || "Link";
      const nameFr = String(item?.name_fr || "").trim() || nameEn;
      const displayName = language === "fr" ? nameFr : nameEn;

      if (!map.has(categoryLabel)) {
        map.set(categoryLabel, []);
      }

      map.get(categoryLabel).push({
        ...item,
        displayName,
        href: normalizeLinkUrl(item?.linkUrl),
      });
    }

    return Array.from(map.entries()).map(([category, items]) => ({
      category,
      items,
    }));
  }, [links, language]);

  const renderIcon = (iconValue) => {
    const icon = String(iconValue || "").trim();
    if (!icon) {
      return <LinkIcon fontSize="small" />;
    }

    if (icon.startsWith("http://") || icon.startsWith("https://")) {
      return (
        <Box
          component="img"
          src={icon}
          alt=""
          sx={{ width: 20, height: 20, objectFit: "contain" }}
        />
      );
    }

    return (
      <MUIIcon
        name={icon}
        size={20}
      />
    );
  };

  return (
    <Container
      maxWidth="md"
      sx={{ py: 4 }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
        >
          <Box
            component="img"
            src="/logo192.png"
            alt="SaSSe"
            sx={{ width: 42, height: 42, objectFit: "contain" }}
          />
          <Typography
            variant="h4"
            component="h1"
            fontWeight="bold"
          >
            {language === "fr" ? "Liens" : "Links"}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
        >
          <Button
            variant={language === "en" ? "contained" : "outlined"}
            onClick={() => setLanguage("en")}
          >
            EN
          </Button>
          <Button
            variant={language === "fr" ? "contained" : "outlined"}
            onClick={() => setLanguage("fr")}
          >
            FR
          </Button>
        </Stack>
      </Stack>

      <Stack
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => setShowPasscodeInput((prev) => !prev)}
            sx={{ fontSize: "0.95rem" }}
          >
            {language === "fr" ? "Liens sécurisés" : "Secured Links"}
          </Link>
        </Box>

        {showPasscodeInput ? (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ maxWidth: 460 }}
          >
            <TextField
              size="small"
              fullWidth
              label={language === "fr" ? "Code d'accès" : "Passcode"}
              value={passcode}
              onChange={(event) => setPasscode(event.target.value)}
              placeholder={
                language === "fr" ? "Entrer le code" : "Enter passcode"
              }
            />
            <Button
              variant="contained"
              onClick={handleUnlock}
            >
              {language === "fr" ? "Débloquer" : "Unlock"}
            </Button>
          </Stack>
        ) : null}

        {unlocked ? (
          <Typography
            variant="body2"
            color="success.main"
          >
            {unlockMessage}
          </Typography>
        ) : unlockMessage ? (
          <Typography
            variant="body2"
            color="error.main"
          >
            {unlockMessage}
          </Typography>
        ) : null}
      </Stack>

      {error ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
        >
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {!loading && grouped.length === 0 ? (
        <Alert severity="info">
          {language === "fr" ? "Aucun lien disponible" : "No links available"}
        </Alert>
      ) : null}

      {!loading
        ? grouped.map((group) => (
            <Paper
              key={group.category}
              variant="outlined"
              sx={{ mb: 2 }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                >
                  {group.category}
                </Typography>
              </Box>
              <Divider />
              <Stack>
                {group.items.map((item, index) => (
                  <Box key={item.id || `${group.category}-${index}`}>
                    <Link
                      href={item.href || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      onClick={(event) => {
                        if (!item.href) {
                          event.preventDefault();
                        }
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.25,
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {renderIcon(item.icon)}
                      </Box>
                      <Typography>{item.displayName}</Typography>
                    </Link>
                    {index < group.items.length - 1 ? <Divider /> : null}
                  </Box>
                ))}
              </Stack>
            </Paper>
          ))
        : null}
    </Container>
  );
};

export default PublicLinks;
